import { bookkeepingSoftwareOptions } from "components/BooksCrm/BookkeepingTrackerSoftware";
import { BookkeepingTrackerTags } from "components/BooksCrm/BookkeepingTrackerTags";
import { ChatPane } from "components/ChatPane/ChatPane";
import { Accordion } from "components/DesignSystem/Accordion/Accordion";
import { Button } from "components/DesignSystem/Button/Button";
import { InfoItem } from "components/DesignSystem/InfoItem/InfoItem";
import { Link } from "components/DesignSystem/Link/Link";
import Loader from "components/DesignSystem/Loader/loader";
import { Notes } from "components/DesignSystem/Notes/Notes";
import Slider from "components/DesignSystem/Slider/Slider";
import Tab from "components/DesignSystem/Tab/Tab";
import DashboardContainer from "components/dashboard/DashboardContainer";
import { FilingTags } from "components/tasks/taskForForeignCA/FilingTags";
import { useModal } from "hooks/useModal";
import { useToast } from "hooks/useToast";
import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useRequestAccountantAccessMutation } from "store/apis/bankConnections";
import { useGetTaskQuery } from "store/apis/task";
import {
  BookkeepingSoftwareType,
  ENTITY_BANK_CONNECTION_DETAILS,
  EntityGroup,
} from "types/Models/bookkeepingTracker";
import { formatDate } from "utils/formatDate";
import { EntityBankConnections } from "./EntityBankConnections";
import { AuditlogList } from "components/AuditlogList/AuditlogList";
import { BackendError } from "types/utils/error";

export const TaskSliderContent = ({
  taskId,
  defaultTab,
  group,
  bookkeepingSoftware,
  connectionDetails,
}: {
  taskId: string;
  defaultTab: "DETAILS" | "CHAT";
  group: EntityGroup;
  bookkeepingSoftware: BookkeepingSoftwareType;
  connectionDetails: ENTITY_BANK_CONNECTION_DETAILS[];
}) => {
  const [tab, setTab] = useState(defaultTab);
  const notesModal = useModal();
  const history = useHistory();
  const [requestAccountantAccess] = useRequestAccountantAccessMutation();
  const { alertToast } = useToast();

  const { data: task, isLoading } = useGetTaskQuery(
    {
      taskId: taskId!,
    },
    { skip: !taskId }
  );

  const [channelId, setChannelId] = useState<string | null>(null);
  const [isRequestingAccess, setIsRequestingAccess] = useState(false);

  const handleRequestAccess = async (item: {
    entity_item_id: string;
    name?: string;
    logo?: string;
  }) => {
    if (!channelId || !task) return;

    setIsRequestingAccess(true);

    try {
      await requestAccountantAccess({
        entityId: task.entity.uuid,
        entityItemIds: [item.entity_item_id],
        channelId,
      }).unwrap();
    } catch (e) {
      alertToast({ message: (e as BackendError).data?.error?.message });
      setIsRequestingAccess(false);
      return;
    }

    setIsRequestingAccess(false);
    history.push(`/admin/chat/${channelId}`);
  };

  const channelUrl =
    typeof task?.channel === "string"
      ? task?.channel
      : task?.channel?.channel_url;

  useEffect(() => {
    if (channelUrl) {
      setChannelId(channelUrl);
    }
  }, [channelUrl]);

  return (
    <Tab.Root
      value={tab}
      asChild
      onValueChange={(v) => setTab(v as typeof tab)}
    >
      <DashboardContainer className="t-h-full">
        <DashboardContainer.Header>
          <Slider.Header
            bottom={
              <Tab.List className="!t-pt-0">
                <Tab.Trigger value="DETAILS">Details</Tab.Trigger>
                <Tab.Trigger value="CHAT">Chat</Tab.Trigger>
                <Tab.Trigger value="ACTIVITY">Activity</Tab.Trigger>
              </Tab.List>
            }
          >
            <Slider.Title>{task?.title}</Slider.Title>
            <Slider.Close />
          </Slider.Header>
        </DashboardContainer.Header>
        <DashboardContainer.Content className="t-h-full t-relative t-top-0">
          <Tab.Content value="DETAILS" asChild>
            {isLoading || !task ? (
              <div className="t-flex t-h-full t-w-full t-justify-center t-items-center">
                <Loader size="small" />
              </div>
            ) : (
              <>
                <Slider.Body>
                  <Accordion.Root
                    type="multiple"
                    defaultValue={[
                      "COMPANY_DETAILS",
                      "FILING_DETAILS",
                      "BANK_DETAILS",
                      "TASK_TIMELINE",
                    ]}
                    className="t-flex t-gap-4 t-flex-col t-mb-16"
                  >
                    <Accordion.Item value="COMPANY_DETAILS">
                      <Accordion.Trigger disabled>
                        Company details
                      </Accordion.Trigger>
                      <Accordion.Content forceMount>
                        <Accordion.ItemGrid>
                          <InfoItem label="Group">
                            <Link to={`/admin/crm/${task.group_uuid}`}>
                              {task.group_name}
                            </Link>
                          </InfoItem>
                          <InfoItem label="Entity">
                            <Link
                              to={`/admin/crm/${task.group_uuid}/entity/${task.entity.uuid}`}
                            >
                              {task.entity.name}
                            </Link>
                          </InfoItem>
                          <InfoItem label="Formation type">
                            {task.entity.formation_type?.name}
                          </InfoItem>
                          <InfoItem label="Company tag">
                            <BookkeepingTrackerTags group={group} />
                          </InfoItem>
                        </Accordion.ItemGrid>
                      </Accordion.Content>
                    </Accordion.Item>

                    <Accordion.Item value="FILING_DETAILS">
                      <Accordion.Trigger>
                        Service and bank details
                      </Accordion.Trigger>
                      <Accordion.Content>
                        <Accordion.ItemGrid>
                          <InfoItem label="Service">
                            <Link to={`/tax/filings/${task?.uuid}`}>
                              {task.title}
                            </Link>
                          </InfoItem>
                          <InfoItem label="Task tag">
                            <FilingTags
                              taskId={task.uuid}
                              currentTags={task.tags}
                              tags={[]}
                            />
                          </InfoItem>
                          <InfoItem label="Service team">
                            {task.assigned_team?.name
                              ? task.assigned_team?.name
                              : "-"}
                          </InfoItem>
                          <InfoItem label="Bookkeeper">
                            {task.assigned_to?.name
                              ? task.assigned_to?.name
                              : "-"}
                          </InfoItem>
                          <InfoItem label="Software">
                            {bookkeepingSoftware
                              ? bookkeepingSoftwareOptions[bookkeepingSoftware]
                                  ?.label
                              : "-"}
                          </InfoItem>
                        </Accordion.ItemGrid>
                      </Accordion.Content>
                    </Accordion.Item>

                    <Accordion.Item value="BANK_DETAILS">
                      <Accordion.Trigger>Bank details</Accordion.Trigger>
                      <Accordion.Content className="t-space-y-6">
                        <EntityBankConnections
                          entityId={task.entity.uuid}
                          connectionDetails={connectionDetails}
                          isRequestingAccess={isRequestingAccess}
                          onRequestAccess={handleRequestAccess}
                        />
                      </Accordion.Content>
                    </Accordion.Item>

                    <Accordion.Item value="TASK_TIMELINE">
                      <Accordion.Trigger>Task timeline</Accordion.Trigger>
                      <Accordion.Content className="t-space-y-6">
                        <InfoItem label="Deadline">
                          {task.deadline ? formatDate(task.deadline) : "-"}
                        </InfoItem>
                        <Accordion.ItemGrid>
                          <InfoItem label="ETD">
                            {task.estimated_time_of_delivery?.date
                              ? formatDate(
                                  task.estimated_time_of_delivery?.date
                                )
                              : "-"}
                          </InfoItem>

                          <InfoItem label="Assigned on">
                            {task.assigned_at
                              ? formatDate(task.assigned_at)
                              : "-"}
                          </InfoItem>
                          <InfoItem label="Started on">
                            {task.bookkeeping_task_start_date
                              ? formatDate(task.bookkeeping_task_start_date)
                              : "-"}
                          </InfoItem>
                          <InfoItem label="Completed on">
                            {task.completed_on
                              ? formatDate(task.completed_on)
                              : "-"}
                          </InfoItem>
                        </Accordion.ItemGrid>
                      </Accordion.Content>
                    </Accordion.Item>
                  </Accordion.Root>
                </Slider.Body>
                <Slider.Footer className="!t-sticky">
                  <Button
                    size="small"
                    block
                    disabled={isLoading || !task}
                    onClick={notesModal.open}
                  >
                    Edit notes
                  </Button>
                </Slider.Footer>
              </>
            )}
          </Tab.Content>

          <Tab.Content value="CHAT" asChild>
            {isLoading || !task ? (
              <div className="t-flex t-h-full t-w-full t-justify-center t-items-center">
                <Loader size="small" />
              </div>
            ) : (
              <ChatPane
                channelId={channelId}
                setChannelId={setChannelId}
                filter={{ entity_id: task.entity.uuid }}
              />
            )}
          </Tab.Content>

          <Tab.Content value="ACTIVITY" asChild>
            <div className="t-pt-5 t-h-full">
              <AuditlogList
                entityId={task?.entity.uuid || ""}
                objectId={task?.uuid || ""}
              />
            </div>
          </Tab.Content>
          {notesModal.isOpen && (
            <Notes
              isOpen={notesModal.isOpen}
              close={notesModal.close}
              objId={task?.uuid || ""}
              commentType="TASK_COMMENT"
              entityId={task?.entity.uuid || ""}
              isPrivate
            />
          )}
        </DashboardContainer.Content>
      </DashboardContainer>
    </Tab.Root>
  );
};

export const BookkeepingTaskSlider = ({
  taskId,
  isOpen,
  onClose,
  defaultTab = "DETAILS",
  group,
  bookkeepingSoftware,
  connectionDetails,
}: {
  taskId?: string | null;
  isOpen: boolean;
  onClose: () => void;
  defaultTab?: "DETAILS" | "CHAT";
  group: EntityGroup;
  bookkeepingSoftware: BookkeepingSoftwareType;
  connectionDetails: ENTITY_BANK_CONNECTION_DETAILS[];
}) => {
  return (
    <Slider.Root open={isOpen} onOpenChange={onClose}>
      <Slider.Content open={isOpen}>
        {taskId && (
          <TaskSliderContent
            defaultTab={defaultTab}
            taskId={taskId}
            group={group}
            bookkeepingSoftware={bookkeepingSoftware}
            connectionDetails={connectionDetails}
          />
        )}
      </Slider.Content>
    </Slider.Root>
  );
};
